<template>
  <div class="w-full md:pl-10 md:w-3/4 md:pr-10">
    <div v-for="group in categoryGroups" :key="group.id" class="mb-8" v-if="categorySlugsByGroupId[group.id] && group.ui_status === 'details'">

      <label ref="categories">
        Event {{ group.title }} <span class="text-red">*</span>
        <span class="block text-sm font-sans-book" v-if="group.max_categories_allowed < 100">
          {{ group.help_text }} You may choose up to {{ group.max_categories_allowed }}.
        </span>
      </label>
      <div class="group__checkbox">
        <div class="flex flex-wrap select-box">
          <template v-for="categorySlug in categorySlugsByGroupId[group.id]">
            <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="categorySlug">
              <input
                :disabled="categoryGroupDisabled(group.id) && !form.categoriesForGroup[group.id].includes(categorySlug)"
                :name="`event ${group.title}`.toLowerCase()"
                type="checkbox"
                v-validate="'required'"
                v-model="form.categoriesForGroup[group.id]"
                :value="categorySlug"
              />
              <span class="inline-block mr-2 check"></span> {{ categoriesBySlug[categorySlug].title }}
            </label>
          </template>
          <span
            v-show="errors.has(`event ${group.title}`.toLowerCase())"
            class="text-lg error text-error-red">
              {{ errors.first(`event ${group.title}`.toLowerCase()) }}
          </span>
        </div>
      </div>
    </div>

    <div v-for="group in categoryGroups" :key="group.id" class="mb-8" v-if="user.isAdmin && categorySlugsByGroupId[group.id] && group.ui_status === 'details_admin'">
      <label ref="categories">
        Event {{ group.title }} (admin only)
      </label>
      <div class="group__checkbox">
        <div class="flex flex-wrap select-box">
          <template v-for="categorySlug in categorySlugsByGroupId[group.id]">
            <label class="w-full text-base font-normal md:w-1/2 md:pr-3" :key="categorySlug">
              <input
                :name="`event ${group.title}`.toLowerCase()"
                type="checkbox"
                v-model="form.categoriesForGroup[group.id]"
                :value="categorySlug"
              />
              <span class="inline-block mr-2 check"></span> {{ categoriesBySlug[categorySlug].title }}
            </label>
          </template>
        </div>
      </div>
    </div>

    <div class="mb-6">
      <label for="image" ref="image">
        Main Event Image <span class="text-red">*</span>
        <span class="mb-5 text-sm font-sans-book block">
          Please upload a horizontal/landscape image (JPEG or PNG), ideally 1424 pixels x 862 pixels.
          Image must NOT have any text.
        </span>
      </label>

      <div v-if="event && event.image">
        <img alt="display image here" :src="event.image" class="h-48 mb-2"/>
      </div>

      <input id="image" type="hidden" name="image" v-validate="imageValidate" v-model="form.newImage">

      <vue-dropzone
        ref="dropzone"
        id="dropzone"
        :options="dropzoneOptions"
        @vdropzone-success="uploaded"
        @vdropzone-error="updateImageErrorMessage"
        @vdropzone-removed-file="resetImage"
        :useCustomSlot=true
      >
        <div class="after-drop-message">
          <p>You may drag-and-drop an image here. File size must be less than 4 MB.</p>
        </div>
      </vue-dropzone>
      <span v-if="errors.has('image')" class="text-lg error text-error-red">{{ errors.first('image') }}</span>
      <span v-if="imageError" class="text-lg error text-error-red">{{ imageError }}</span>

    </div>

    <div class="mb-6">
      <label for="teaser" ref="teaser">
        Teaser <span class="text-red">*</span>
        <span class="block text-sm font-sans-book">
          200 characters maximum (including spaces). This brief teaser copy is your
          listing on our event calendar schedule page (alongside other events). It should be enticing and make it clear to
          attendees what your particular event is about.
        </span>
      </label>
      <textarea
        v-model="form.teaser"
        v-validate="teaserValidate"
        name="teaser"
        id="teaser"
        class="w-full h-48 p-4 mt-3 overflow-y-scroll bg-transparent border border-black"
      >
      </textarea>

      <div class="text-sm font-sans-italic text-gray-dark">
        Character count: {{ characterCount }}
      </div>
      <span v-show="errors.has('teaser')" class="text-lg error text-error-red">
        {{ errors.first('teaser') }}
      </span>
    </div>

    <div class="mb-6">
      <label for="description" ref="description">
        Description <span class="text-red">*</span>
        <span class="block text-sm font-sans-book">
          1000 characters maximum (including spaces). This full description is for your unique event details page.
          Here, you can expand on your event information and add a bit of context
          related to the host(s), or any other details to excite potential attendees (e.g., speaker/performer names and
          bios, etc.) You also can add hyperlinks, where appropriate. NOTE: Teaser copy will appear at the top of your
          event details page, so do not repeat this exact content in your full description.
        </span>
      </label>
      <vue-editor
        v-model="form.description"
        :editorToolbar="descriptionToolbar"
        v-validate="'required'"
        name="description"
        id="description">
      </vue-editor>

      <span v-show="errors.has('description')" class="text-lg error text-error-red">
        {{errors.first('description') }}
      </span>
    </div>

    <div class="mb-6 relative">
      <label for="cohosts">
        Co-hosts
        <span class="block text-sm font-sans-book mb-4">Search and select names of organizations or individuals to credit as event co-hosts. If not listed, please have them create an account at portal.letsreimagine.org. You can add up to three co-hosts by clicking the '+' sign. For any problems, email us at hello@reimagine.org.</span>
      </label>

      <div class="mb-6">
        <ul class="list-none p-0">
          <li v-for="(cohost, index) in form.cohosts" :key="index" class="flex pb-2 mb-4 border-b-2 border-slate-500 justify-between items-center">
            {{ cohost.name }} <button @click="removeCohost(index)" class="button-icon py-2 px-3">x</button>
          </li>
        </ul>
      </div>

      <div class="flex mb-2" v-if="form.cohosts.length < 3">
        <cohosts-search v-model="cohostInput" :clear="clearSearch"></cohosts-search>
        <button @click="addCohost" class="button-icon h-12">ADD</button>
      </div>
    </div>

    <div class="mb-6 flex flex-wrap justify-start lg:justify-end">
      <button
        class="button"
        @click.stop.prevent="save"
        :class="{ 'opacity-50 cursor-not-allowed': saving }"
        :disabled="saving">
        <template v-if="saving">Processing...</template>
        <template v-else>Save</template>
      </button>
      <div class="mr-0 lg:mr-4 mb-4 lg:mb-4 w-full lg:w-auto" />
      <button
        class="button"
        @click.stop.prevent="saveNext"
        :class="{ 'opacity-50 cursor-not-allowed': saving }"
        :disabled="saving">
        Save & Next
      </button>
    </div>

  </div>
</template>

<script>
import {mapGetters} from "vuex";
import eventSave from "../../../mixins/event-save";
// noinspection ES6UnusedImports, ES6CheckImport,ES6UnusedImports
import {VueEditor, Quill} from "vue2-editor";
import messaging from "../../../mixins/messaging";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import validation from "../../../mixins/validation";
import Cookies from 'js-cookie';
import NewTabA11y from "../../global/a11y/NewTabA11y.vue";
import CohostsSearch from "../CohostsSearch.vue";
// import _ from 'lodash'

const link = Quill.import('formats/link');
link.sanitize = function (url = '') {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

export default {
  name: "EventCreateDetails",
  title: "Create Event Details",
  components: {
    NewTabA11y,
    VueEditor,
    CohostsSearch,
    vueDropzone: vue2Dropzone,
  },
  mixins: [eventSave, messaging, validation],
  data: () => ({
    descriptionToolbar: [
      ["bold", "italic", "link"],
      [{list: "ordered"}, {list: "bullet"}]
    ],
    form: {
      categories: [],
      categoriesForGroup: {},
      newImage: "",
      teaser: "",
      description: "",
      cohosts: [{
        name: '',
        id: null
      }],
    },
    cohostInput: {},
    clearSearch: false,
    imageError: "",
    next: "events-tickets",
  }),
  watch: {
    event(event) {
      if (!_.isEmpty(event)) {
        this.form.teaser = event.teaser;
        this.form.description = event.description;
        this.form.cohosts = event.cohosts;

        this.handleEventCategoryUpdate();

        this.$nextTick(function () {
          this.$store.dispatch("setUnsavedData", false);
        });
      }

      this.$nextTick(() => this.$validator.reset());
    },
    categoryGroups() {
      this.handleEventCategoryUpdate();
    },
  },
  computed: {
    ...mapGetters({
      event: "event",
      tokens: "tokens",
      categoryGroups: 'categoryGroups',
      categoryGroupsById: 'categoryGroupsById',
      categoriesBySlug: 'categoriesBySlug',
      categorySlugsByGroupId: 'categorySlugsByGroupId',
      user: 'user',
    }),
    characterCount() {
      return this.form.teaser ? this.form.teaser.length : 0;
    },
    dropzoneOptions() {
      return {
        addRemoveLinks: true,
        acceptedFiles: ".png,.jpg, image/png, image/jpeg",
        headers: {
          "X-Xsrf-Token": Cookies.get('XSRF-TOKEN'),
          Authorization: "Bearer " + this.tokens.access_token
        },
        maxFiles: 1,
        maxFilesize: 4, // in Mb
        thumbnailWidth: "247",
        thumbnailHeight: "143",
        url: this.$laroute.route("api.temp-images.store"),
        withCredentials: true
      };
    },
    imageValidate() {
      if ((this.event && this.event.image) || this.form.newImage) {
        return "";
      }
      return "required";
    },
    teaserValidate() {
      if(this.user.isAdmin) {
        return 'required';
      }

      return 'required|max:200';
    },
    hasCohost() {
      return ! _.isEmpty(this.cohostInput) && this.cohostInput.id === 'undefined'
    }
  },
  methods: {
    handleEventCategoryUpdate () {
      let formGroups = this.categoryGroups.reduce((acc, group) => {
        acc[group.id] = this.getCategoriesForGroup(group.id);
        return acc;
      }, {  });

      this.$set(this.form, 'categoriesForGroup', formGroups);
      this.$store.dispatch("setUnsavedData", false);
    },
    getCategoriesForGroup (groupId) {
      let categories = _.map(this.event.categories, "slug")
      let categorySlugsForGroup = this.categorySlugsByGroupId[groupId] || [];

      return categories.filter(
        slug => categorySlugsForGroup.includes(slug)
      );
    },
    addCohost() {
      this.$store.commit('ADD_COHOST', this.cohostInput)
      this.cohostInput = {}
      this.clearSearch = true
    },
    removeCohost(index) {
      this.$store.commit('REMOVE_COHOST', index)
    },
    categoryGroupDisabled(groupId) {
      let categoryGroup = this.categoryGroupsById[groupId]

      if (!this.form.categoriesForGroup[groupId]) {
        return false;
      }

      return this.form.categoriesForGroup[groupId].length >= categoryGroup.max_categories_allowed
    },
    resetImage() {
      this.imageError = "";

      axios
        .delete(this.$laroute.route("api.temp-images.destroy"), {
          data: {filePath: this.form.newImage}
        })
        .then(() => {
          this.form.newImage = "";
        })
        .catch(error => {
          this.displayError(error);
        });
    },
    combineCategories() {
      const categories = [];
      Object.values(this.form.categoriesForGroup).forEach(categoryList => categories.push(...categoryList))
      return categories;
    },
    save() {
      this.saving = true;
      this.$store.dispatch("setEventIsLoading", true);

      return this.$validator.validateAll().then(result => {
        if (!result) {
          this.saving = false;
          this.gotoFirstValidationError();
          return;
        }

        this.$toasted.show("Event currently saving...");

        let route = this.$laroute.route("api.events.update.details", {
          event: this.event.id
        });

        const query = {
          ...this.form,
          categories: this.combineCategories()
        };

        query.categoriesForGroup = undefined;

        return axios
          .put(route, query)
          .then(response => {
            this.showUpdatedToast();
            this.saving = false;

            let eventResponse = response.data.data;
            this.$store.dispatch("setValidation", eventResponse);

            this.$store.dispatch("setUnsavedData", false);
            this.$store.dispatch("setEventHasErrors", false);
            this.$store.dispatch("setEventIsLoading", false);

            this.$refs.dropzone.removeAllFiles();

            this.$store.dispatch("getEvent", {
              id: this.event.id
            });
          })
          .catch(error => {
            this.displayError(error);
          });
      });
    },
    updateImageErrorMessage(file, message, xhr) {
      if (parseInt(xhr.status) === 419) {
        this.$router.push({
          name: "user-login"
        });
      }
      this.imageError = message;
    },
    uploaded(file, path) {
      this.form.newImage = path;
    }
  },
  mounted() {
    this.$store.dispatch("getEvent", {id: this.$route.params.id});
    this.$store.dispatch("getCategories");
    this.$store.dispatch("getThemes");
    document.getElementById("dropdown").checked = false;
  }
};
</script>
